// src/hooks/useCandidates.js
import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getCandidates, addCandidate, updateCandidate, deleteCandidate, addAgent, getAgents, deleteAgent, revertCandidateHire,
} from '../db/index.js'
import { logEvent, getCurrentAdmin, logCandidateEvent } from '../lib/agentLog.js'
import { toDateStr } from '../lib/attendance.js'
import { seedCandidatesIfEmpty } from '../db/seed.js'

import { t, tLog } from '../lib/i18n.js'

export const CANDIDATE_STATUSES = {
  new:         { get label() { return t('cand_status_new')        }, labelKa: 'ახალი',              color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',             dot: 'bg-blue-500'    },
  scheduled:   { get label() { return t('cand_status_scheduled')  }, labelKa: 'დაგეგმილი',          color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',     dot: 'bg-violet-500'  },
  interviewed: { get label() { return t('cand_status_interviewed')}, labelKa: 'გასაუბრება გავიდა',  color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',         dot: 'bg-amber-500'   },
  // no_show: scheduled interview that they didn't come to. NOT terminal — reschedule
  // (new date → 'scheduled'), reject, or hire all remain available. Requires the
  // widened DB CHECK on candidates.status (see supabase-schema.sql, 2026-07-11).
  no_show:     { get label() { return t('cand_status_no_show')    }, labelKa: 'არ გამოცხადდა',      color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',     dot: 'bg-orange-500'  },
  starting:    { get label() { return t('cand_status_starting')   }, labelKa: 'იწყებს',             color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',             dot: 'bg-cyan-500'    },
  hired:       { get label() { return t('cand_status_hired')      }, labelKa: 'დაქირავებული ✓',     color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', dot: 'bg-emerald-500' },
  rejected:    { get label() { return t('cand_status_rejected')   }, labelKa: 'უარყოფილი',          color: 'bg-muted text-muted-foreground border-border',                                 dot: 'bg-muted-foreground' },
}

// `me` = the caller's own agent row, resolved in App.jsx from the authenticated session
// (auth_user_id === session.user.id). Optional: an admin without an agent record still gets
// a byline, from the audit-log name.
export function useCandidates(offices = [], me = null) {
  const [candidates, setCandidates] = useState([])
  const [loading,    setLoading]    = useState(true)
  const [error,      setError]      = useState(null)

  const candidatesRef     = useRef(candidates)
  useEffect(() => { candidatesRef.current     = candidates     }, [candidates])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      await seedCandidatesIfEmpty()
      const data = await getCandidates()
      setCandidates(data)
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Authorship is stamped HERE, not in the form (owner ask, 2026-08-29 — "when manager adds
  // candidate to be seen there who added it"). Two reasons it is not a form field:
  //   • create() is the ONE creation path, so nothing can be added without a byline;
  //   • a form field is editable, and "who added this" must not be, or it stops being a fact.
  // Both columns are written: created_by is the joinable truth, created_by_name a snapshot
  // that still renders once the adder resigns. getCurrentAdmin() is the fallback name only —
  // it is a localStorage string, so it labels, it does not identify.
  const create = useCallback(async (data) => {
    const c = await addCandidate({
      ...data,
      status: data.status || 'new',
      created_by:      me?.id   || null,
      created_by_name: me?.name || getCurrentAdmin(),
      // The office of whoever is adding them (owner ruling 2026-08-29). Recorded HERE, at the
      // one moment it is unambiguous: the manager sourcing the candidate knows which team the
      // person is for. The hire then inherits it, so an admin pressing the button later cannot
      // redirect them. See db/scheduler-migrations/0004.
      office_id:       me?.office_id || null,
    })
    // Keep the ref in sync synchronously so a follow-up promote (hire-on-create) finds it
    candidatesRef.current = [c, ...candidatesRef.current]
    setCandidates(prev => [c, ...prev])
    logEvent(`cand_${c.id}`, c.name, 'info',
      tLog('log_cand_added', { by: getCurrentAdmin(), position: c.position || '—', source: c.source || '—' }))
    logCandidateEvent(c.id, 'added',
      tLog('log_cand_added_short', {
        position: c.position ? tLog('log_diff_for', { position: c.position }) : '',
        source:   c.source   ? tLog('log_diff_via', { source: c.source })     : '',
      }))
    if (c.interview_date) {
      logCandidateEvent(c.id, 'scheduled',
        tLog('log_cand_scheduled', { by: getCurrentAdmin(), date: c.interview_date, time: c.interview_time || '—' }))
    }
    return c
    // me is a dependency for real: it resolves asynchronously after the session loads, and a
    // create() frozen with the null it started as would file the row with no byline.
  }, [me?.id, me?.name])

  const update = useCallback(async (id, updates) => {
    const prev = candidatesRef.current.find(c => c.id === id)
    const c    = await updateCandidate(id, updates)
    setCandidates(list => list.map(x => x.id === id ? c : x))
    const by = getCurrentAdmin()

    if (updates.status && updates.status !== prev?.status) {
      logEvent(`cand_${id}`, c.name, 'info',
        tLog('log_cand_status', { from: CANDIDATE_STATUSES[prev?.status]?.label || prev?.status, to: CANDIDATE_STATUSES[updates.status]?.label || updates.status, by }))
    }
    const hadDate = prev?.interview_date
    const hasDate = updates.interview_date
    if (!hadDate && hasDate) {
      logCandidateEvent(id, 'scheduled',
        tLog('log_cand_scheduled', { by, date: hasDate, time: updates.interview_time || '—' }))
      logEvent(`cand_${id}`, c.name, 'schedule',
        tLog('log_cand_scheduled', { by, date: hasDate, time: updates.interview_time || '—' }))
    }
    if (hadDate && hasDate && hadDate !== hasDate) {
      logCandidateEvent(id, 'rescheduled', tLog('log_cand_rescheduled', { from: hadDate, to: hasDate + (updates.interview_time ? tLog('log_diff_at_time', { time: updates.interview_time }) : '') }))
      logEvent(`cand_${id}`, c.name, 'schedule', tLog('log_cand_rescheduled_full', { by, from: hadDate, to: hasDate }))
    }
    // "Cancelled" only makes sense for a still-PLANNED interview. A no_show's date is a
    // completed historical event — clearing it (e.g. mid-reschedule) must not log a
    // cancellation of something that already happened. 2026-07-11.
    if (hadDate && updates.interview_date === null && prev?.status === 'scheduled') {
      logCandidateEvent(id, 'cancelled', tLog('log_cand_cancelled', { date: hadDate }))
    }
    if (updates.status && updates.status !== prev?.status) {
      if (updates.status === 'interviewed') {
        // 60s undo window (same pattern as hire_ts_*): the "undo interviewed" button in
        // the modal footer / quick menu only shows while this timestamp is fresh.
        try { localStorage.setItem(`interviewed_ts_${id}`, String(Date.now())) } catch {}
        logCandidateEvent(id, 'interviewed', tLog('log_cand_interviewed', {
          date:        prev?.interview_date ? tLog('log_diff_interviewed_on', { date: prev.interview_date + (prev.interview_time ? tLog('log_diff_at_time', { time: prev.interview_time }) : '') }) : '',
          interviewer: prev?.interviewer    ? tLog('log_diff_with', { name: prev.interviewer }) : '',
        }))
      }
      if (updates.status === 'starting') {
        logCandidateEvent(id, 'note', tLog('log_cand_starting', {
          date: updates.start_date || '',
          time: updates.start_time ? tLog('log_diff_at_time', { time: updates.start_time }) : '',
        }))
      }
      if (updates.status === 'no_show') {
        logCandidateEvent(id, 'no_show', tLog('log_cand_no_show', { date: prev?.interview_date || '—' }))
      }
      if (updates.status === 'rejected') {
        logCandidateEvent(id, 'rejected', tLog('log_cand_rejected', { reason: updates.notes ? tLog('log_diff_reason', { reason: updates.notes }) : '' }))
      }
    }
    return c
  }, [])

  const remove = useCallback(async (id) => {
    const c = candidatesRef.current.find(x => x.id === id)
    if (c) logEvent(`cand_${id}`, c.name, 'info', tLog('log_cand_deleted', { by: getCurrentAdmin() }))
    await deleteCandidate(id)
    setCandidates(prev => prev.filter(x => x.id !== id))
  }, [])

  /**
   * Promote candidate to agent — ONLY if not already hired.
   * Guards against duplicate agent creation.
   */
  const promoteToAgent = useCallback(async (candidateId) => {
    const c = candidatesRef.current.find(x => x.id === candidateId)
    if (!c) throw new Error('Candidate not found')

    // ── Guard: already hired → just navigate to agents ──────────────────────
    if (c.status === 'hired' && c.hired_agent_id) {
      return null
    }

    const by = getCurrentAdmin()

    const agentData = {
      name:                c.name,
      role:                c.position      || 'Agent',
      email:               c.email         || '',
      phone_work:          '',
      phone_personal:      c.phone         || '',
      district:            c.district      || null,
      languages:           Array.isArray(c.languages) && c.languages.length > 0 ? c.languages : null,
      hire_date:           toDateStr(new Date()),
      is_active:           true,
      // ⚠️ NEVER offices[0] AGAIN. That was "the first office in whatever list this screen had
      // loaded", which is an arbitrary team when the list was there and NULL when it was not —
      // and a NULL office makes the agent invisible to every manager at once, because managers
      // are locked to their own office and the filter is `a.office_id === officeFilter`, which
      // NULL never matches. That is how `Aleksandra` (2026-08-28) ended up unreachable to her
      // own manager and duplicated by hand three hours later.
      // Order of truth: who recruited them → who is hiring them → the only office there is.
      // If the company has more than one office and neither is known, leave it NULL rather
      // than misfile them: unassigned is visible to admin and fixable, a wrong office is not
      // visible as wrong to anyone.
      office_id:           c.office_id || me?.office_id || (offices.length === 1 ? offices[0].id : null),
      candidate_source_id: c.id,  // dedup key — prevents double hire
      schedule:            { Mon:[], Tue:[], Wed:[], Thu:[], Fri:[], Sat:[], Sun:[] },
    }

    const agent = await addAgent(agentData)

    // Mark candidate as hired — store pre_hire_status for revert
    const updated = await updateCandidate(candidateId, {
      status:           'hired',
      hired_agent_id:   agent.id,
      pre_hire_status:  c.status,
    })
    setCandidates(list => list.map(x => x.id === candidateId ? updated : x))

    logCandidateEvent(candidateId, 'hired', tLog('log_cand_hired_short'))
    logEvent(`cand_${candidateId}`, c.name, 'hired', tLog('log_cand_hired', { by }))
    logEvent(agent.id, agent.name, 'hired',
      tLog('log_cand_hired_from_pipeline', {
        by,
        interviewed: c.interview_date ? tLog('log_diff_interviewed_was', { date: c.interview_date }) : '',
        interviewer: c.interviewer    ? tLog('log_diff_with', { name: c.interviewer })               : '',
        notes:       c.notes          ? tLog('log_diff_notes', { notes: c.notes })                   : '',
      }))

    return { ...agent, _candidateId: candidateId }
    // Real dependencies, not decoration: `me` resolves asynchronously after the session loads
    // and `offices` after its own fetch. Frozen with the nulls they start as, a hire would file
    // the agent with no office — reintroducing exactly the invisible-to-every-manager record
    // this change exists to prevent.
  }, [me?.office_id, offices])

  const revertHire = useCallback(async (candidateId) => {
    const c = candidatesRef.current.find(x => x.id === candidateId)
    if (!c || !c.hired_agent_id) return
    const updated = await revertCandidateHire(candidateId, c.hired_agent_id, c.pre_hire_status)
    setCandidates(list => list.map(x => x.id === candidateId ? updated : x))
  }, [])

  return { candidates, loading, error, reload: load, create, update, remove, promoteToAgent, revertHire }
}
